import { FC, useState } from "react";
import { useTrans } from "../../../hooks";
import { useAuthData } from "../../../contexts";
import {
    Button,
    Input,
    InputGroup,
    InputLeftElement,
    SimpleGrid,
    VStack,
} from "@chakra-ui/react";
import { Company, CompanyApi, School, SchoolApi } from "../../../apis";
import { Controller, useForm } from "react-hook-form";
import { AppFilterContainer } from "../../../containers";
import {
    AppFormControl,
    AppIcon,
    AppMultiSelector,
    AppMultiSelectorOption,
    AppMultiSelectorOptionType,
} from "../../../components";
import { UTIL } from "../../../config";

export type coordinatorRequestFilterDataType = {
    coordinator_request_search: string;
};

const defaultValues: coordinatorRequestFilterDataType = {
    coordinator_request_search: "",
};

export const CoordinatorRequestFilter: FC<{
    filterData: any;
    setFilterData: (data: any) => void;
    children: JSX.Element;
}> = ({ filterData, setFilterData, children }) => {
    // hook
    const { t } = useTrans();
    const { user, grant } = useAuthData();

    // state & const
    const [selectedSchools, setSelectedSchools] = useState<
        AppMultiSelectorOption[]
    >([]);
    const [selectedCompanies, setSelectedCompanies] = useState<
        AppMultiSelectorOption[]
    >([]);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const { handleSubmit, reset, control } =
        useForm<coordinatorRequestFilterDataType>({
            defaultValues: filterData,
            mode: "onSubmit",
        });

    // handler
    const onSubmitHandler = (formData: coordinatorRequestFilterDataType) => {
        const filterParams = {};
        Object.keys(formData).forEach((key) => {
            if (formData[key] || formData[key] === false) {
                filterParams[key] = formData[key];
            }
        });
        if (selectedSchools.length > 0 && selectedSchools[0].value) {
            filterParams["school.id"] = selectedSchools[0].value;
        }
        if (selectedCompanies.length > 0 && selectedCompanies[0].value) {
            filterParams["company.id"] = selectedCompanies[0].value;
        }
        setFilterData(filterParams);
    };

    const loadSchoolOptions = async (
        page: number,
        search: string,
        pageSize: number
    ): Promise<AppMultiSelectorOptionType> => {
        const params = {
            itemsPerPage: pageSize,
            [`order[name]`]: "asc",
            pagination: true,
        };

        if (search) {
            params["name"] = search;
        }

        const { response } = await SchoolApi.getCollection<School>(
            page,
            params
        );

        if (response !== null) {
            const newOptions =
                response.items?.map((e) => ({
                    value: SchoolApi.toResourceIRI(e.id),
                    label: `${e.id} ${e.name}`,
                })) || [];
            return {
                data: newOptions,
                totalItems: response.totalItems,
            };
        }

        return { data: [], totalItems: 0 };
    };

    const loadCompanyOptions = async (
        page: number,
        search: string,
        pageSize: number
    ): Promise<AppMultiSelectorOptionType> => {
        const params = {
            itemsPerPage: pageSize,
            [`order[name]`]: "asc",
            pagination: true,
        };

        if (search) {
            params["name"] = search;
        }

        const { response } = await CompanyApi.getCollection<Company>(
            page,
            params
        );

        if (response !== null) {
            const newOptions =
                response.items?.map((e) => ({
                    value: CompanyApi.toResourceIRI(e.id),
                    label: `${e.id} ${e.name}`,
                })) || [];
            return {
                data: newOptions,
                totalItems: response.totalItems,
            };
        }

        return { data: [], totalItems: 0 };
    };

    const resetForm = () => {
        setFilterData({});
        setSelectedSchools([]);
        setSelectedCompanies([]);
        reset(defaultValues);
    };

    return (
        <AppFilterContainer
            onReset={resetForm}
            triggerElement={children}
            isModalOpen={isModalOpen}
            zIndex={"1400"}
        >
            <form
                id="app-filter-form"
                onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit(onSubmitHandler)();
                }}
            >
                <VStack gap={4} alignItems={"stretch"} justify={"start"}>
                    <AppFormControl label={t("cmn:label.search")}>
                        <Controller
                            control={control}
                            name={"coordinator_request_search"}
                            defaultValue={
                                filterData?.coordinator_request_search || ""
                            }
                            render={({ field }) => (
                                <InputGroup>
                                    <InputLeftElement pointerEvents="none">
                                        <AppIcon name="ics-search" w="1rem" />
                                    </InputLeftElement>
                                    <Input {...field} />
                                </InputGroup>
                            )}
                        />
                    </AppFormControl>
                    <SimpleGrid
                        columns={{ sm: 1, md: 2 }}
                        spacingX={5}
                        spacingY={4}
                        w={"full"}
                    >
                        {grant.isSuperAdmin && (
                            <>
                                <AppFormControl
                                    label={t("ent.User:school.label")}
                                >
                                    <AppMultiSelector
                                        onSelect={(data) => {
                                            setSelectedSchools(data);
                                            setIsModalOpen(false);
                                        }}
                                        loadOptions={loadSchoolOptions}
                                        selectedOpt={selectedSchools}
                                        subject={t("ent.User:school.label")}
                                        triggerElement={
                                            <Button
                                                size={"sm"}
                                                variant={"secondary"}
                                                onClick={() =>
                                                    setIsModalOpen(true)
                                                }
                                            >
                                                {t(
                                                    "padm.UserForm:button.selectSchool"
                                                )}
                                            </Button>
                                        }
                                        isModal={true}
                                        isSearch={true}
                                        selectorRows={8}
                                        maxSelectOptions={
                                            UTIL.isSchoolCompanyMultiSelect(
                                                user?.userType
                                            )
                                                ? undefined
                                                : 1
                                        }
                                        setIsModalOpen={setIsModalOpen}
                                    />
                                </AppFormControl>
                                <AppFormControl
                                    label={t("ent.User:company.label")}
                                >
                                    <AppMultiSelector
                                        onSelect={(data) => {
                                            setSelectedCompanies(data);
                                            setIsModalOpen(false);
                                        }}
                                        loadOptions={loadCompanyOptions}
                                        selectedOpt={selectedCompanies}
                                        subject={t("ent.User:company.label")}
                                        triggerElement={
                                            <Button
                                                size={"sm"}
                                                variant={"secondary"}
                                                onClick={() =>
                                                    setIsModalOpen(true)
                                                }
                                            >
                                                {t(
                                                    "padm.UserForm:button.selectCompany"
                                                )}
                                            </Button>
                                        }
                                        isModal={true}
                                        isSearch={true}
                                        selectorRows={8}
                                        maxSelectOptions={
                                            UTIL.isSchoolCompanyMultiSelect(
                                                user?.userType
                                            )
                                                ? undefined
                                                : 1
                                        }
                                        setIsModalOpen={setIsModalOpen}
                                    />
                                </AppFormControl>
                            </>
                        )}
                    </SimpleGrid>
                </VStack>
            </form>
        </AppFilterContainer>
    );
};
